import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export interface CartItem {
  id: string;
  name: string;
  artisan: string;
  price: number; // In EUR
  quantity: number;
  image: string;
  originalPrice?: number;
  inStock?: boolean;
  isGiftCard?: boolean;
  is_digital?: boolean; // Digital product flag
  sellerId?: string; // ID of the seller for this item
  shipping_profile_id?: string; // 'free', 'small', 'medium', 'large'
  variant_id?: string; // ID of the selected variant
  variant_attributes?: Record<string, string>; // e.g., { "Color": "Red", "Size": "Small" }
  variant_label?: string; // Display label like "Red - Small"
  giftCardData?: {
    amount_cents: number;
    currency: string;
    recipient_email: string;
    recipient_name?: string;
    sender_name?: string;
    personal_message?: string;
    delivery_date?: string;
  };
}

interface CartContextValue {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string, variantId?: string) => void;
  updateQuantity: (id: string, quantity: number, variantId?: string) => void;
  clear: () => void;
  subtotal: number;
  count: number;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const STORAGE_KEY = "perlla_cart";

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = (item: CartItem) => {
    setItems((prev) => {
      // For variant products, match by product ID + variant ID
      // For non-variant products, match by product ID only
      const existing = prev.find((i) => 
        i.id === item.id && 
        (item.variant_id ? i.variant_id === item.variant_id : !i.variant_id)
      );
      
      if (existing) {
        return prev.map((i) =>
          i.id === item.id && 
          (item.variant_id ? i.variant_id === item.variant_id : !i.variant_id)
            ? { ...i, quantity: i.quantity + item.quantity } 
            : i
        );
      }
      return [...prev, item];
    });
  };

  const removeItem = (id: string, variantId?: string) => 
    setItems((prev) => prev.filter((i) => 
      !(i.id === id && (variantId ? i.variant_id === variantId : !i.variant_id))
    ));

  const updateQuantity = (id: string, quantity: number, variantId?: string) => {
    setItems((prev) => {
      if (quantity <= 0) {
        return prev.filter((i) => 
          !(i.id === id && (variantId ? i.variant_id === variantId : !i.variant_id))
        );
      }
      return prev.map((i) => 
        i.id === id && (variantId ? i.variant_id === variantId : !i.variant_id)
          ? { ...i, quantity } 
          : i
      );
    });
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value: CartContextValue = { items, addItem, removeItem, updateQuantity, clear, subtotal, count };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
